import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { cn } from "@/lib/utils";
import {
  enableAutoSuggest,
  bucketUrlPrefix,
  enableGooglePoiClick,
} from "common/config";
import {
  getSuggestions,
  getEnrichedPlacePredictions,
  Suggestion,
  RecentSearchResult,
} from "common/apis";
import { useFilters, useI18N, useBrands } from "common/hooks";
import { trackEvent } from "common/analytics";
import {
  SearchSuggestionsDropdown,
  type NeighborhoodSuggestion,
} from "@/components/Search/FilterPannel/SearchSuggestions";
import { MagnifyingGlassIcon, MapPinIcon, NavigationArrowIcon, TrainIcon, AirplaneTakeoffIcon } from "@phosphor-icons/react";
import { CookieIcon, CoffeeIcon, MartiniIcon, ForkKnifeIcon, ShoppingCartIcon} from "@phosphor-icons/react";


// Re-export types for external use
export type { RecentSearchResult, NeighborhoodSuggestion };

type AutoSuggestInputProps = {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: (text: string) => void;
  onSelectSuggestion: (suggestion: Suggestion) => void;
  onClear?: () => void;
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  className?: string;
  tagging_mode?: string;
  types?: string;
  accessibilities?: string;
  showVenueOptions?: string;
  // SearchSuggestionsDropdown callbacks
  onNearbyPlacesClick?: () => void;
  onRecentSearchSelect?: (search: RecentSearchResult) => void;
  onNeighborhoodSelect?: (neighborhood: NeighborhoodSuggestion) => void;
  onLoginPress?: () => void;
  /** Whether to show the SearchSuggestionsDropdown when input is empty/focused */
  showSearchSuggestions?: boolean;
  /** Suppress the autosuggest dropdown (e.g., when saved searches is open) */
  suppressDropdown?: boolean;
  /** Layout mode - determines positioning and wrapper behavior */
  layout?: "default" | "header" | "overlay" | "overlaymobile";
};

// Helper function to sanitize input by removing HTML tags and normalizing whitespace
const sanitizeInput = (text: string): string => {
  const withoutHtml = text.replace(/<[^>]*>/g, "");
  return withoutHtml.trim().replace(/\s+/g, " ");
};

// Helper function to format text by removing underscores and capitalizing each word
const formatTextForDisplay = (text: string): string => {
  if (!text) return "";

  // Replace underscores with spaces
  const withoutUnderscores = text.replace(/_/g, " ");

  // Capitalize each word
  return withoutUnderscores
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Helper function to format display text based on suggestion type
const formatDisplayText = (
  suggestion: Suggestion,
  currentValue: string,
): string => {
  if (suggestion.suggestion_type === "brand") {
    // For brand suggestions, use the brand name with formatting
    return formatTextForDisplay(suggestion.brand || currentValue);
  } else if (suggestion.suggestion_type === "location") {
    // For location suggestions, use the text
    return sanitizeInput(suggestion.text || "");
  } else if (suggestion.suggestion_type === "business_type") {
    // For business_type suggestions, use the business type with formatting
    return formatTextForDisplay(suggestion.business_type || currentValue);
  } else if (suggestion.suggestion_type === "compound_search") {
    // For compound suggestions, only show the category term ("bar"), never
    // "bar, wembley" — the location part is only used for geocoding internally.
    return sanitizeInput(suggestion.name || suggestion.text || "");
  } else if (
    suggestion.suggestion_type === "google_area" ||
    suggestion.suggestion_type === "google_station" ||
    suggestion.suggestion_type === "google_airport"
  ) {
    // For area/transit suggestions, show just the place name without city
    // (the city is shown in the subtitle, not the search bar).
    return sanitizeInput(suggestion.name || suggestion.text || currentValue);
  } else {
    // For other suggestions, use name and city
    const nameSan = sanitizeInput(
      suggestion.name || suggestion.text || currentValue,
    );
    const citySan = sanitizeInput(suggestion.city || "");
    return citySan ? `${nameSan}, ${citySan}` : nameSan;
  }
};

// Helper function to get the appropriate icon based on suggestion type
const getSuggestionIcon = (
  suggestionType?: string,
  businessTypeValue?: string | null,
  brandName?: string | null,
  latitude?: number,
  longitude?: number,
  pinType?: "orange" | "grey" | null,
) => {
  // "Places" suggestions (our venues + Google). In-DB venues use the real Sociability
  // pin (with the "S" mark) so orange/grey is obvious and matches the map; Google-only
  // places that aren't on Sociability get a plain outline pin.
  if (suggestionType === "google_place" || suggestionType === "venue_name") {
    if (pinType === "orange") {
      return (
        <img
          src="/pins/orange_pin.svg"
          alt=""
          aria-hidden="true"
          className="h-6 w-auto flex-shrink-0"
        />
      );
    }
    if (pinType === "grey") {
      return (
        <img
          src="/pins/grey_pin.svg"
          alt=""
          aria-hidden="true"
          className="h-6 w-auto flex-shrink-0"
        />
      );
    }
    // venue_name is always a Sociability venue — default to grey pin even
    // when pinType is null (e.g. less-complete venues not yet fully assessed).
    if (suggestionType === "venue_name") {
      return (
        <img
          src="/pins/grey_pin.svg"
          alt=""
          aria-hidden="true"
          className="h-6 w-auto flex-shrink-0"
        />
      );
    }
    // google_place with no pinType — plain outline pin (not in Sociability DB)
    return (
      <MapPinIcon
        size={20}
        className="text-fg-neutral-subtle flex-shrink-0"
      />
    );
  }

  // For brand suggestions, use the brand logo component
  if (suggestionType === "brand" && brandName) {
    return (
      <BrandLogo
        brandName={brandName}
        latitude={latitude || 0}
        longitude={longitude || 0}
      />
    );
  }

  // For business_type and venue_name suggestions, use the business type icon from the filters
  if (
    (suggestionType === "business_type" || suggestionType === "venue_name") &&
    businessTypeValue
  ) {
    return <BusinessTypeIcon businessType={businessTypeValue} />;
  }

  // Compound search ("restaurant near Covent Garden") — magnifying glass signals intent
  if (suggestionType === "compound_search") {
    return (
      <MagnifyingGlassIcon size={20} className="text-fg-neutral-medium flex-shrink-0" />
    );
  }

  // Google area/neighbourhood result — navigation arrow distinguishes it from our MapPin
  if (suggestionType === "google_area") {
    return (
      <NavigationArrowIcon size={20} className="text-fg-neutral-medium flex-shrink-0" />
    );
  }

  // Transit station — train icon signals "transport, not a venue"
  if (suggestionType === "google_station") {
    return (
      <TrainIcon size={20} className="text-fg-neutral-medium flex-shrink-0" />
    );
  }

  // Airport — plane icon, distinct from train stations
  if (suggestionType === "google_airport") {
    return (
      <AirplaneTakeoffIcon size={20} className="text-fg-neutral-medium flex-shrink-0" />
    );
  }

  // For other suggestion types, use the default icons
  switch (suggestionType) {
    case "location":
      return (
        <MapPinIcon size={20} className="text-fg-brand-subtle flex-shrink-0" />
      );
    default:
      return (
        <MapPinIcon size={20} className="text-fg-brand-subtle flex-shrink-0" />
      );
  }
};

// Regex pattern for "category near/in/around/at/by location" compound queries.
const COMPOUND_SEARCH_RE = /^(.{2,}?)\s+(?:near|in|around|at|by)\s+(.{2,}.*)$/i;

/**
 * Try to match a user-typed category term against our known business type list.
 * Tries (in order): exact → starts-with → type-contains-term → term-contains-type.
 * Returns the canonical business type name or null when no match is found.
 */
function matchBusinessType(
  term: string,
  businessTypes: { name: string }[],
): string | null {
  const t = term.toLowerCase().trim();
  if (!t || t.length < 2 || !businessTypes?.length) return null;
  const exact = businessTypes.find((bt) => bt.name.toLowerCase() === t);
  if (exact) return exact.name;
  const startsWith = businessTypes.find((bt) => bt.name.toLowerCase().startsWith(t));
  if (startsWith) return startsWith.name;
  const contains = businessTypes.find((bt) => bt.name.toLowerCase().includes(t));
  if (contains) return contains.name;
  const termContains = businessTypes.find((bt) => t.includes(bt.name.toLowerCase()));
  if (termContains) return termContains.name;
  return null;
}

// Component to display business type icons
const BusinessTypeIcon = ({ businessType }: { businessType: string }) => {
  const { businessTypesFrontend } = useFilters();
  const [iconPath, setIconPath] = useState<string | null>(null);

  // First check for specific hardcoded business type icons
  const hasSpecificIcon = ["bakery", "cafe", "bar", "restaurant", "supermarket", "food","store"].includes(businessType);

  // Find the matching business type and get its icon from businessTypesFrontend
  useEffect(() => {
    // Skip fetching if we have a specific icon
    if (hasSpecificIcon) return;
    
    if (businessTypesFrontend && businessType) {
      const matchingFrontendType = businessTypesFrontend.find(
        (type) => type.name.toLowerCase() === businessType.toLowerCase(),
      );
      if (matchingFrontendType && matchingFrontendType.image) {
        setIconPath(bucketUrlPrefix + matchingFrontendType.image);
        return;
      }
    }
  }, [businessTypesFrontend, businessType, hasSpecificIcon]);

  // Render specific icon if available
  if (hasSpecificIcon) {
    switch (businessType) {
      case "bakery":
        return <CookieIcon size={20} className="text-fg-brand-subtle" />;
      case "cafe":
        return <CoffeeIcon size={20} className="text-fg-brand-subtle" />;
      case "bar":
        return <MartiniIcon size={20} className="text-fg-brand-subtle" />;
      case "restaurant":
        return <ForkKnifeIcon size={20} className="text-fg-brand-subtle" />;
      case "food":
          return <ShoppingCartIcon size={20} className="text-fg-brand-subtle" />;
      case "supermarket":
        return <ShoppingCartIcon size={20} className="text-fg-brand-subtle" />;
      case "store":
        return <ShoppingCartIcon size={20} className="text-fg-brand-subtle" />;
    }
  }

  // Render dynamic icon from businessTypesFrontend if found
  if (iconPath) {
    return (
      <img
        src={iconPath}
        alt={businessType}
        className="w-6 h-6 object-contain filter brightness-0"
      />
    );
  }

  // Fallback icon if no matching business type is found
  return <MapPinIcon size={20} className="text-fg-brand-subtle flex-shrink-0" />;
};

// Component to display brand logo or first character
interface BrandLogoProps {
  brandName: string;
  latitude: number;
  longitude: number;
}

const BrandLogo = ({ 
  brandName, 
  latitude, 
  longitude 
}: BrandLogoProps) => {
  const { data: brandsData } = useBrands({
    latitude,
    longitude,
    enabled: !!brandName && !!latitude && !!longitude && !isNaN(latitude) && !isNaN(longitude)
  });
  
  const matchingBrand = brandsData?.facets?.brand?.find(
    b => b.value.toLowerCase() === brandName.toLowerCase()
  );
  
  const imageUrl = matchingBrand?.logo 
    ? `${bucketUrlPrefix}${matchingBrand.logo}` 
    : null;
    
  return (
    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-container-neutral-minimal overflow-hidden flex-shrink-0">
      {imageUrl ? (
        <img src={imageUrl} alt={brandName} className="w-full h-full object-cover" />
      ) : (
        <span className="label-s-semibold text-fg-neutral-strong">
          {brandName.charAt(0).toUpperCase()}
        </span>
      )}
    </div>
  );
};

export default function AutoSuggestInput({
  label,
  value,
  onValueChange,
  onSubmit,
  onSelectSuggestion,
  onClear,
  latitude = 0,
  longitude = 0,
  radiusKm = 10,
  className,
  tagging_mode,
  types,
  accessibilities,
  showVenueOptions,
  // SearchSuggestionsDropdown callbacks
  onNearbyPlacesClick,
  onRecentSearchSelect,
  onNeighborhoodSelect,
  onLoginPress,
  showSearchSuggestions = false,
  suppressDropdown = false,
  layout = "default" as "default" | "header" | "overlay" | "overlaymobile",
}: AutoSuggestInputProps) {
  const i18n = useI18N();
  const { businessTypesFrontend } = useFilters();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const [isSearchSuggestionsOpen, setIsSearchSuggestionsOpen] = useState(false);

  // search_list_closed: fire once per open->closed transition of the empty-input search-list overlay
  // (counterpart to search_list_viewed on open). The overlay closes from many call sites (dismiss,
  // start-typing, select, search), so edge-detect on the state rather than wiring each site. Per
  // product decision it fires on EVERY close. The ref makes it StrictMode-safe.
  const searchListOpenRef = useRef(false);
  useEffect(() => {
    if (searchListOpenRef.current && !isSearchSuggestionsOpen) {
      trackEvent("search_list_closed", {});
    }
    searchListOpenRef.current = isSearchSuggestionsOpen;
  }, [isSearchSuggestionsOpen]);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const suggestDebounceRef = useRef<number | null>(null);
  const suggestListRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input when component mounts
  useEffect(() => {
    if (layout === "overlay" || layout === "overlaymobile") {
      // Small delay to ensure DOM is ready and any animations have started
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [layout]);

  // Close dropdowns when suppressDropdown becomes true
  useEffect(() => {
    if (suppressDropdown) {
      setIsSuggestOpen(false);
      setIsSearchSuggestionsOpen(false);
    }
  }, [suppressDropdown]);

  useEffect(() => {
    if (!isSuggestOpen) return;
    if (highlightIndex < 0) return;
    const activeEl = document.getElementById(`suggest-item-${highlightIndex}`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex, isSuggestOpen]);

  const displayItems = useMemo(() => {
    const items: Array<
      { kind: "query"; text: string } | { kind: "suggestion"; data: Suggestion }
    > = [];
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      items.push({ kind: "query", text: trimmed });
    }

    // Detect compound queries like "restaurant near Covent Garden".
    // Only active when Google POI features are enabled (same feature flag).
    let compoundSuggestion: Suggestion | null = null;
    let suppressedBusinessType: string | null = null;
    if (enableGooglePoiClick && trimmed.length >= 5) {
      const m = trimmed.match(COMPOUND_SEARCH_RE);
      if (m) {
        const [, categoryPart, locationQuery] = m;
        const matched = matchBusinessType(categoryPart, businessTypesFrontend ?? []);
        suppressedBusinessType = matched;
        compoundSuggestion = {
          id: null,
          slug: null,
          place_id: null,
          // `name` = category term (primary display, highlighted against typed value)
          // `address` = location query string (used for geocoding at click time)
          // `city`    = location part (shown in subtitle)
          // `business_type` = matched canonical type, or null for text-search fallback
          name: categoryPart,
          address: locationQuery,
          city: locationQuery,
          business_type: matched,
          latitude: null,
          longitude: null,
          completeness: null,
          text: categoryPart,
          distanceKm: null,
          distanceText: null,
          suggestion_type: "compound_search" as const,
          inDb: false,
          pinType: null,
        };
      }
    }

    // Track where the last "places" item sits so the compound suggestion
    // can be inserted immediately before the categories section.
    let lastPlaceIdx = 0; // default: right after the query item
    for (const s of suggestions) {
      // Suppress the business-type chip that the compound suggestion already covers.
      if (
        suppressedBusinessType &&
        s.suggestion_type === "business_type" &&
        s.business_type?.toLowerCase() === suppressedBusinessType.toLowerCase()
      ) {
        continue;
      }
      const idx = items.length;
      if (s.suggestion_type === "google_place" || s.suggestion_type === "venue_name") {
        lastPlaceIdx = idx;
      }
      items.push({ kind: "suggestion", data: s });
    }

    // Inject the compound suggestion as the first item in "Categories & Areas".
    if (compoundSuggestion) {
      const insertAt = lastPlaceIdx + (lastPlaceIdx > 0 ? 1 : 1);
      items.splice(insertAt, 0, { kind: "suggestion", data: compoundSuggestion });
    }

    return items;
  }, [value, suggestions, businessTypesFrontend]);

  const clearSuggestions = useCallback(() => {
    if (suggestDebounceRef.current) {
      window.clearTimeout(suggestDebounceRef.current);
      suggestDebounceRef.current = null;
    }
    setSuggestions([]);
    setIsSuggestOpen(false);
    setIsSearchSuggestionsOpen(false);
    setHighlightIndex(-1);
  }, []);

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (isSuggestOpen || isSearchSuggestionsOpen) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        clearSuggestions();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSuggestOpen, isSearchSuggestionsOpen, clearSuggestions]);

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightName = (name: string, query: string) => {
    if (!name) return <>{name}</>;
    const tokens = query
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 2);
    if (tokens.length === 0) return <>{name}</>;
    const pattern = tokens.map(escapeRegExp).join("|");
    const re = new RegExp(`(${pattern})`, "ig");
    const parts = name.split(re);
    return (
      <>
        {parts.map((part, idx) =>
          re.test(part) ? (
            <b key={idx}>{part}</b>
          ) : (
            <span key={idx}>{part}</span>
          ),
        )}
      </>
    );
  };

  // Fetch our DB suggestions and (when enabled) Google place predictions in
  // parallel, then append a "Places" section, deduped against DB venues by place_id.
  const fetchMergedSuggestions = useCallback(
    async (text: string): Promise<Suggestion[]> => {
      const [dbList, predictions] = await Promise.all([
        getSuggestions(text, {
          fuzzy: true,
          highlights: true,
          latitude,
          longitude,
          radius: radiusKm,
          size: 8,
          tagging_mode,
          types,
          accessibilities,
          showVenueOptions,
        }),
        enableGooglePoiClick
          ? getEnrichedPlacePredictions(text, { latitude, longitude })
          : Promise.resolve([]),
      ]);

      if (!enableGooglePoiClick) {
        // Reorder so venues (Places) come before categories/locations, preventing
        // the "Categories & Areas" heading from appearing twice when the API returns
        // business_type → venue_name → location in that order.
        const dbVenues = dbList.filter((s) => s.suggestion_type === "venue_name");
        const dbOther = dbList.filter((s) => s.suggestion_type !== "venue_name");
        return [...dbVenues, ...dbOther];
      }

      // Split our DB suggestions: venues feed the combined Places section, the rest
      // (brands / business types) stay as a separate categories section.
      // Location suggestions are excluded here — Google areas are used instead
      // as they provide higher-quality, more consistent area results.
      const dbVenues = dbList.filter((s) => s.suggestion_type === "venue_name");
      const categorySection = dbList.filter(
        (s) => s.suggestion_type !== "venue_name" && s.suggestion_type !== "location",
      );

      // Separate venue predictions from area/neighbourhood/station/airport predictions.
      const venuePredictions = predictions.filter(
        (p) =>
          p.suggestionType !== "google_area" &&
          p.suggestionType !== "google_station" &&
          p.suggestionType !== "google_airport",
      );
      const areaPredictions = predictions.filter(
        (p) => p.suggestionType === "google_area",
      );
      const stationPredictions = predictions.filter(
        (p) => p.suggestionType === "google_station",
      );
      const airportPredictions = predictions.filter(
        (p) => p.suggestionType === "google_airport",
      );

      const googlePlaces: Suggestion[] = venuePredictions
        .filter((p) => p.place_id)
        .map((p) => ({
          id: p.businessId != null ? String(p.businessId) : null,
          slug: p.slug,
          place_id: p.place_id,
          name: p.mainText,
          address: p.secondaryText,
          city: null,
          business_type: null,
          latitude: null,
          longitude: null,
          completeness: null,
          text: p.mainText,
          distanceKm: p.distanceMeters != null ? p.distanceMeters / 1000 : null,
          distanceText:
            p.distanceMeters != null
              ? `${(p.distanceMeters / 1000).toFixed(1)} km`
              : null,
          suggestion_type: "google_place" as const,
          inDb: p.inDb,
          pinType: p.pinType,
        }));

      // Google area predictions land in the "Categories & Areas" section.
      // Azure location suggestions are excluded in favour of these (see categorySection
      // filter above), so no deduplication against Azure results is needed.
      // Store secondaryText in `city` so it's available for the subtitle renderer.
      const googleAreas: Suggestion[] = areaPredictions
        .filter((p) => p.place_id)
        .map((p) => ({
          id: null,
          slug: null,
          place_id: p.place_id,
          name: p.mainText,
          address: p.secondaryText || null,
          city: p.secondaryText || null,
          business_type: null,
          latitude: null,
          longitude: null,
          completeness: null,
          text: p.mainText,
          distanceKm: null,
          distanceText: null,
          suggestion_type: "google_area" as const,
          inDb: false,
          pinType: null,
        }));

      // Transit station predictions also land in "Categories & Areas" — no pin is
      // created; the map pans to the station and runs a nearby search.
      const googleStations: Suggestion[] = stationPredictions
        .filter((p) => p.place_id)
        .map((p) => ({
          id: null,
          slug: null,
          place_id: p.place_id,
          name: p.mainText,
          address: p.secondaryText || null,
          city: p.secondaryText || null,
          business_type: null,
          latitude: null,
          longitude: null,
          completeness: null,
          text: p.mainText,
          distanceKm: p.distanceMeters != null ? p.distanceMeters / 1000 : null,
          distanceText:
            p.distanceMeters != null
              ? `${(p.distanceMeters / 1000).toFixed(1)} km`
              : null,
          suggestion_type: "google_station" as const,
          inDb: false,
          pinType: null,
        }));

      // Combine venues from both sources into one Places list. Drop DB venues whose
      // place_id Google already returned (keep the Google entry), then sort nearest-
      // first (entries without a distance go last).
      const googlePlaceIds = new Set(
        googlePlaces.map((p) => p.place_id).filter(Boolean) as string[],
      );
      const dbVenuesNotInGoogle = dbVenues.filter(
        (v) => !v.place_id || !googlePlaceIds.has(v.place_id),
      );
      const placesSection = [...googlePlaces, ...dbVenuesNotInGoogle].sort(
        (a, b) => {
          const da = a.distanceKm ?? Number.POSITIVE_INFINITY;
          const db = b.distanceKm ?? Number.POSITIVE_INFINITY;
          return da - db;
        },
      );

      // Airport predictions land in "Categories & Areas" — same pan+nearby behaviour
      // as stations but with a plane icon and "Search near this airport" subtitle.
      const googleAirports: Suggestion[] = airportPredictions
        .filter((p) => p.place_id)
        .map((p) => ({
          id: null,
          slug: null,
          place_id: p.place_id,
          name: p.mainText,
          address: p.secondaryText || null,
          city: p.secondaryText || null,
          business_type: null,
          latitude: null,
          longitude: null,
          completeness: null,
          text: p.mainText,
          distanceKm: p.distanceMeters != null ? p.distanceMeters / 1000 : null,
          distanceText:
            p.distanceMeters != null
              ? `${(p.distanceMeters / 1000).toFixed(1)} km`
              : null,
          suggestion_type: "google_airport" as const,
          inDb: false,
          pinType: null,
        }));

      // Places first, then categories & areas (DB results + Google areas + stations + airports).
      return [...placesSection, ...categorySection, ...googleAreas, ...googleStations, ...googleAirports];
    },
    [
      latitude,
      longitude,
      radiusKm,
      tagging_mode,
      types,
      accessibilities,
      showVenueOptions,
    ],
  );

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    onValueChange(text);

    if (!enableAutoSuggest || suppressDropdown) return;

    if (suggestDebounceRef.current) {
      window.clearTimeout(suggestDebounceRef.current);
    }

    if (text && text.length >= 1) {
      // Close SearchSuggestionsDropdown and open autosuggest when user starts typing
      setIsSearchSuggestionsOpen(false);
      setIsSuggestOpen(true);
      setHighlightIndex(0);

      // Fetch suggestions only when length >= 2
      if (text.length >= 2) {
        suggestDebounceRef.current = window.setTimeout(async () => {
          const list = await fetchMergedSuggestions(text);
          setSuggestions(list);
          setIsSuggestOpen(true);
          // keep highlight at 0 (typed query)
        }, 200);
      }
    } else {
      // When input is cleared, show SearchSuggestionsDropdown if enabled
      clearSuggestions();
      if (showSearchSuggestions) {
        setIsSearchSuggestionsOpen(true);
      }
    }
  };

  const handleClear = () => {
    // clear_action_button -> search_cleared (reset the query without dismissing the surface).
    trackEvent("search_cleared", {});
    onValueChange("");
    clearSuggestions();
    if (onClear) onClear();
  };

  const handleEnter = () => {
    // If list is open, Enter will be handled in onKeyDown (using highlightIndex)
    if (isSuggestOpen) return;
    clearSuggestions();
    onSubmit(value);
  };

  const handleFocus = useCallback(async () => {
    if (suppressDropdown) return;

    const text = value.trim();

    // If input is empty and showSearchSuggestions is enabled, show SearchSuggestionsDropdown
    if (text.length === 0 && showSearchSuggestions) {
      // search_bar_input_field -> search_list_viewed (the search list overlay opens).
      trackEvent("search_list_viewed", {});
      setIsSearchSuggestionsOpen(true);
      setIsSuggestOpen(false);
      return;
    }

    if (!enableAutoSuggest) return;

    // Only show suggestions if there's text in the input
    if (text.length >= 2) {
      setIsSearchSuggestionsOpen(false);
      setIsSuggestOpen(true);
      setHighlightIndex(0);

      // Fetch suggestions for the existing text
      const list = await fetchMergedSuggestions(text);

      setSuggestions(list);
    } else if (text.length === 1) {
      // Just show the query item without fetching suggestions
      setIsSearchSuggestionsOpen(false);
      setIsSuggestOpen(true);
      setHighlightIndex(0);
      setSuggestions([]);
    }
  }, [
    value,
    latitude,
    longitude,
    radiusKm,
    tagging_mode,
    types,
    accessibilities,
    showVenueOptions,
    showSearchSuggestions,
    suppressDropdown,
    fetchMergedSuggestions,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle arrow keys for SearchSuggestionsDropdown (when input is empty)
    if (isSearchSuggestionsOpen && value.length === 0) {
      // SearchSuggestionsDropdown handles its own keyboard navigation via global listener
      return;
    }
    
    if (!enableAutoSuggest) return;
    if (!isSuggestOpen || displayItems.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length > 0) {
        setIsSuggestOpen(true);
        setHighlightIndex((prev) =>
          prev < 0 ? 0 : (prev + 1) % suggestions.length,
        );
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      setHighlightIndex((prev) =>
        prev < 0 ? 0 : (prev + 1) % displayItems.length,
      );
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setHighlightIndex((prev) =>
        prev <= 0 ? displayItems.length - 1 : prev - 1,
      );
      e.preventDefault();
    } else if (e.key === "Enter") {
      const indexToUse = highlightIndex >= 0 ? highlightIndex : 0;
      const item = displayItems[indexToUse];
      if (item) {
        if (suggestDebounceRef.current) {
          window.clearTimeout(suggestDebounceRef.current);
          suggestDebounceRef.current = null;
        }
        setIsSuggestOpen(false);
        setHighlightIndex(-1);
        setSuggestions([]);
        if (item.kind === "query") {
          onSubmit(sanitizeInput(value));
        } else {
          // Format the display text based on suggestion type
          const displayText = formatDisplayText(item.data, value);

          onSelectSuggestion({
            ...item.data,
            text: displayText,
            name: displayText,
          });
        }
      }
      e.preventDefault();
      e.stopPropagation();
    } else if (e.key === "Escape") {
      clearSuggestions();
      e.preventDefault();
    }
  };

  const handleClickItem = (
    item:
      | { kind: "query"; text: string }
      | { kind: "suggestion"; data: Suggestion },
  ) => {
    if (suggestDebounceRef.current) {
      window.clearTimeout(suggestDebounceRef.current);
      suggestDebounceRef.current = null;
    }
    setIsSuggestOpen(false);
    setHighlightIndex(-1);
    setSuggestions([]);
    if (item.kind === "query") {
      onSubmit(sanitizeInput(value));
    } else {
      // Format the display text based on suggestion type
      const displayText = formatDisplayText(item.data, value);

      onSelectSuggestion({
        ...item.data,
        text: displayText,
        name: displayText,
      });
    }
  };

  // Handlers for SearchSuggestionsDropdown
  const handleNearbyPlacesClick = () => {
    clearSuggestions();
    onNearbyPlacesClick?.();
  };

  const handleRecentSearchSelect = (search: RecentSearchResult) => {
    clearSuggestions();
    onRecentSearchSelect?.(search);
  };

  const handleNeighborhoodSelect = (neighborhood: NeighborhoodSuggestion) => {
    clearSuggestions();
    onNeighborhoodSelect?.(neighborhood);
  };

  const handleLoginPress = () => {
    clearSuggestions();
    onLoginPress?.();
  };

  const handleSearchSuggestionsClose = () => {
    setIsSearchSuggestionsOpen(false);
  };

  const handleContainerBlur = () => {
    // Use setTimeout to allow the browser to determine the new activeElement
    setTimeout(() => {
      // CheckIcon if focus moved outside the container
      if (
        containerRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        clearSuggestions();
      }
    }, 0);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)} onBlur={handleContainerBlur}>
      <FloatingLabelInput
        ref={inputRef}
        label={label}
        type="text"
        value={value}
        onChange={handleChange}
        onClear={handleClear}
        onEnter={handleEnter}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        borderless={true}
        role="combobox"
        aria-expanded={
          (isSuggestOpen && value.length > 0) || 
          (isSearchSuggestionsOpen && value.length === 0)
        }
        aria-controls={
          isSuggestOpen && value.length > 0 
            ? "suggestions-listbox" 
            : isSearchSuggestionsOpen && value.length === 0
            ? "search-suggestions-listbox"
            : undefined
        }
        aria-activedescendant={highlightIndex >= 0 ? `suggest-item-${highlightIndex}` : undefined}
        aria-autocomplete="list"
      />

      {/* SearchSuggestionsDropdown - shown when input is empty and focused */}
      {showSearchSuggestions &&
        isSearchSuggestionsOpen &&
        value.length === 0 && (
          <div className="absolute z-20 left-[-30px] md:left-0 right-0 top-full mt-2">
            <SearchSuggestionsDropdown
              isOpen={true}
              onNearbyPlacesClick={handleNearbyPlacesClick}
              onRecentSearchSelect={handleRecentSearchSelect}
              onNeighborhoodSelect={handleNeighborhoodSelect}
              onLoginPress={handleLoginPress}
              onClose={handleSearchSuggestionsClose}
              onKeyboardNavigate={() => {}}
              latitude={latitude}
              longitude={longitude}
            />
          </div>
     )}

      {/* Autosuggest dropdown - shown when user is typing */}
      {enableAutoSuggest && isSuggestOpen && value.length > 0 && (
        <div
          ref={suggestListRef}
          id="suggestions-listbox"
          role="listbox"
          className="absolute z-50 left-0 right-0 top-full mt-2 bg-bg-brand-secondary-default border border-border-token-neutral-minimal rounded shadow-lg max-h-80 overflow-y-auto"
        >
          {displayItems.map((item, idx) => {
            // Section header when the group changes (Places vs Categories & areas).
            let sectionLabel: string | null = null;
            if (item.kind === "suggestion") {
              // Venues form the "Places" group; areas, stations, categories, and
              // compound searches go into "Categories & areas".
              const groupFor = (t?: string) =>
                t === "google_place" || t === "venue_name"
                  ? "places"
                  : "categories";
              const group = groupFor(item.data.suggestion_type);
              const prev = displayItems[idx - 1];
              const prevGroup =
                prev && prev.kind === "suggestion"
                  ? groupFor(prev.data.suggestion_type)
                  : null;
              if (group !== prevGroup) {
                sectionLabel =
                  group === "places"
                    ? i18n.get("places")
                    : i18n.get("categoriesAndAreas");
              }
            }
            return (
            <Fragment key={`item-${idx}`}>
            {sectionLabel && (
              <div className="px-4 pt-3 pb-1 label-s-semibold text-fg-neutral-subtle uppercase tracking-wide">
                {sectionLabel}
              </div>
            )}
            <button
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={idx === highlightIndex}
              className={cn(
                "w-full text-left px-4 py-2 focus:outline-none transition-colors",
                idx === highlightIndex
                  ? "bg-bg-brand-tertiary-active "
                  : "hover:bg-container-neutral-minimal",
              )}
              id={`suggest-item-${idx}`}
              onClick={() => handleClickItem(item)}
            >
              {item.kind === "query" ? (
                <div className="flex items-center gap-2 w-full">
                  <div className="flex items-center justify-center w-6 h-6 flex-shrink-0 min-w-[24px]">
                    <MagnifyingGlassIcon className="text-fg-brand-subtle" size={20} />
                  </div>
                  <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                    <span className="label-m-semibold text-fg-neutral-strong truncate max-w-full">
                      {value}
                    </span>
                    <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                      {i18n.get("pressEnterToSearch")}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 w-full">
                  <div className="flex items-center justify-center w-7 h-7 flex-shrink-0 min-w-[28px]">
                    {getSuggestionIcon(
                      item.data.suggestion_type,
                      item.data.business_type,
                      item.data.brand,
                      latitude,
                      longitude,
                      item.data.pinType,
                    )}
                  </div>
                  <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                    <span className="label-m-semibold text-fg-neutral-strong truncate max-w-full">
                      {item.data.suggestion_type === "brand"
                        ? highlightName(
                            formatTextForDisplay(item.data.brand || ""),
                            value,
                          )
                        : item.data.suggestion_type === "location"
                          ? highlightName(
                              sanitizeInput(item.data.text || ""),
                              value,
                            )
                          : item.data.suggestion_type === "business_type"
                            ? highlightName(
                                formatTextForDisplay(
                                  item.data.business_type || "",
                                ),
                                value,
                              )
                            : highlightName(
                                item.data.name || item.data.text || "",
                                value,
                              )}
                    </span>
                    {item.data.suggestion_type === "brand" && (
                      <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                        {i18n.get("seeLocations")}
                      </span>
                    )}
                    {item.data.suggestion_type === "business_type" && (
                      <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                        {item.data.city_display || item.data.city || ""}
                      </span>
                    )}
                    {item.data.suggestion_type === "google_area" && (
                      <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                        {item.data.city
                          ? `${item.data.city} \u2022 ${i18n.get("searchThisArea")}`
                          : i18n.get("searchThisArea")}
                      </span>
                    )}
                    {(item.data.suggestion_type === "google_station" ||
                      item.data.suggestion_type === "google_airport") && (
                      <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                        {[
                          item.data.distanceText,
                          item.data.city || item.data.address,
                        ]
                          .filter(Boolean)
                          .join(" \u2022 ")}
                        {(item.data.distanceText || item.data.city || item.data.address)
                          ? ` \u2022 ${i18n.get(item.data.suggestion_type === "google_airport" ? "searchNearAirport" : "searchNearStation")}`
                          : i18n.get(item.data.suggestion_type === "google_airport" ? "searchNearAirport" : "searchNearStation")}
                      </span>
                    )}
                    {item.data.suggestion_type === "compound_search" && (
                      <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                        {item.data.city
                          ? `near ${item.data.city} \u2022 ${i18n.get("searchInThisArea")}`
                          : i18n.get("searchInThisArea")}
                      </span>
                    )}
                    {item.data.suggestion_type !== "brand" &&
                      item.data.suggestion_type !== "location" &&
                      item.data.suggestion_type !== "business_type" &&
                      item.data.suggestion_type !== "google_area" &&
                      item.data.suggestion_type !== "google_station" &&
                      item.data.suggestion_type !== "google_airport" &&
                      item.data.suggestion_type !== "compound_search" && (
                        <span className="label-s-medium text-fg-neutral-subtle truncate max-w-full">
                          {item.data.distanceText
                            ? `${item.data.distanceText} \u2022`
                            : ""}
                          {[item.data.address, item.data.city]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      )}
                  </div>
                </div>
              )}
            </button>
            </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
